import { RECORDS, type DataRecord } from '@/data/records';
import { useSyncExternalStore } from 'react';
import { createEntries, diffRecord, seedHistory, type HistoryEntry } from './history-model';

// 리스트·상세가 같은 항목 상태를 보도록 모듈 단위 저장소에 둔다 — 상세에서 편집하고
// 리스트로 돌아와도 반영돼 있어야 한다. 백엔드 연동 전 더미 상태라 새로고침하면 초기화된다.

let snapshot: DataRecord[] = RECORDS.map((record) => ({ ...record }));
// 변경 이력은 항목 id별로 쌓는다(오래된 것 → 최신 순). 처음에는 더미 이력을 깔아 둔다.
let history: Record<string, HistoryEntry[]> = Object.fromEntries(
  RECORDS.map((record, index) => [record.id, seedHistory(record, index)]),
);
const listeners = new Set<() => void>();
const NO_HISTORY: HistoryEntry[] = [];

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit() {
  listeners.forEach((listener) => {
    listener();
  });
}

/** 컴포넌트 밖(가짜 서버 등)에서 현재 항목을 읽는다. */
export function getRecords() {
  return snapshot;
}

export function useRecords() {
  return useSyncExternalStore(subscribe, () => snapshot);
}

/** 항목 하나의 변경 이력. 없으면 항상 같은 빈 배열을 돌려줘 불필요한 재렌더를 막는다. */
export function useRecordHistory(id: string) {
  return useSyncExternalStore(subscribe, () => history[id] ?? NO_HISTORY);
}

/** 항목 하나에 변경을 적용한다. */
export function patchRecord(id: string, patch: Partial<DataRecord>) {
  snapshot = snapshot.map((record) => (record.id === id ? { ...record, ...patch } : record));
  emit();
}

/** 항목 변경을 적용하면서, 실제로 값이 달라진 필드마다 이력을 한 줄씩 남긴다. */
export function saveRecord(id: string, patch: Partial<DataRecord>, actor = '나') {
  const current = snapshot.find((record) => record.id === id);
  if (!current) {
    return;
  }
  const entries = createEntries(id, diffRecord(current, patch), actor, new Date());
  if (entries.length > 0) {
    history = { ...history, [id]: [...(history[id] ?? NO_HISTORY), ...entries] };
  }
  patchRecord(id, patch);
}
